import { useCallback, useEffect, useRef, useState } from 'react';

import { client } from '@/_shared/lib/rpc';
import { slugifyWorkspaceName } from '@/_shared/lib/slugify';

export type SlugStatus = 'idle' | 'checking' | 'available' | 'taken' | 'invalid';

const SLUG_MAX_LEN = 30;

/**
 * Try the base slug and a few numeric suffixes, returning the first available candidate.
 * The suffix is appended within the 30-char limit: a 30-char base is truncated so `<base>-2`
 * still fits (and stays valid — no trailing hyphen), instead of producing an over-length
 * candidate the backend rejects, which would dead-end the user on "Pick a different slug".
 */
async function resolveAvailableSlug(base: string): Promise<{ slug: string; available: boolean }> {
  const suffixed = (n: number): string => {
    const suffix = `-${n}`;
    return `${base.slice(0, SLUG_MAX_LEN - suffix.length).replace(/-+$/, '')}${suffix}`;
  };
  const candidates = base ? [base, suffixed(2), suffixed(3), suffixed(4), suffixed(5)] : [];
  for (const candidate of candidates) {
    try {
      const result = await client.workspace.checkSlug({ slug: candidate });
      if (result.available) return { slug: candidate, available: true };
    } catch {
      // Network hiccup — fall through to returning the base as unresolved.
      break;
    }
  }
  return { slug: base, available: false };
}

/** The Workspace URL field of the picker's create forms: the slug, its availability, and how it is filled. */
export function useWorkspaceSlug(): {
  slug: string;
  status: SlugStatus;
  message: string;
  change: (value: string) => void;
  prefill: (name: string) => Promise<void>;
  fix: (value: string) => void;
} {
  const [slug, setSlug] = useState('');
  const [status, setStatus] = useState<SlugStatus>('idle');
  const [message, setMessage] = useState('');
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  // Monotonic token for the in-flight prefill: only the latest one may apply its async result, so quickly
  // switching rows can't let a stale result clobber the UI.
  const seqRef = useRef(0);

  const check = useCallback(async (value: string) => {
    if (value.length < 2) {
      setStatus('idle');
      setMessage('');
      return;
    }
    setStatus('checking');
    try {
      const result = await client.workspace.checkSlug({ slug: value });
      if (result.available) {
        setStatus('available');
        setMessage('');
      } else {
        setStatus(result.reason?.includes('taken') ? 'taken' : 'invalid');
        setMessage(result.reason ?? 'Unavailable');
      }
    } catch {
      setStatus('invalid');
      setMessage('Could not check availability');
    }
  }, []);

  const change = useCallback(
    (value: string) => {
      const normalized = value.toLowerCase().replace(/[^a-z0-9-]/g, '');
      setSlug(normalized);
      setStatus('idle');
      setMessage('');
      clearTimeout(debounceRef.current);
      if (normalized.length >= 2) {
        debounceRef.current = setTimeout(() => void check(normalized), 300);
      }
    },
    [check],
  );

  /** Fill the slug from a name: the name's slug, else the first free one of four numeric suffixes. */
  const prefill = useCallback(async (name: string) => {
    const seq = ++seqRef.current;
    clearTimeout(debounceRef.current);
    const base = slugifyWorkspaceName(name);
    setSlug(base);
    setStatus('checking');
    setMessage('');
    const { slug: resolved, available } = await resolveAvailableSlug(base);
    // A newer prefill (or re-open) happened while this check was in flight — discard the stale result.
    if (seqRef.current !== seq) return;
    setSlug(resolved);
    if (available) {
      setStatus('available');
    } else {
      setStatus('taken');
      setMessage('Pick a different slug');
    }
  }, []);

  /** A slug that the server decides, such as the URL of a create that has not finished. It is not checked here. */
  const fix = useCallback((value: string) => {
    seqRef.current += 1;
    clearTimeout(debounceRef.current);
    setSlug(value);
    setStatus('available');
    setMessage('');
  }, []);

  useEffect(() => () => clearTimeout(debounceRef.current), []);

  return { slug, status, message, change, prefill, fix };
}
